(() => {
  if (window.EcomhardNecklaceBuilder) return;

  const ICONS = {
    chevronLeft: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>',
    chevronRight: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>',
    rotateLeft: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 8V4m0 0h4M7 4l3 3a8 8 0 1 1-1.9 8.2"/></svg>',
    rotateRight: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M17 8V4m0 0h-4m4 0-3 3a8 8 0 1 0 1.9 8.2"/></svg>',
    trash: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3m-8 0 1 13h8l1-13M10 11v5m4-5v5"/></svg>'
  };

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const normalizeText = (value) => String(value ?? '')
    .trim()
    .toLocaleLowerCase('pt-BR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9#-]/g, '');

  const sameId = (one, two) => String(one) === String(two);

  const makeId = () => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return `eh-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  class NecklaceBuilder {
    constructor(root) {
      this.root = root;
      this.config = this.readConfig();
      if (!this.config) return;

      this.modal = root.querySelector('[data-builder-modal]');
      this.backdrop = root.querySelector('[data-builder-backdrop]');
      this.minimized = root.querySelector('[data-builder-minimized]');
      this.exitConfirmation = root.querySelector('[data-builder-exit-confirmation]');
      this.modalSurface = root.querySelector('[data-builder-modal-surface]');
      this.visualPanel = root.querySelector('[data-builder-visual-panel]');
      this.selectionPanel = root.querySelector('[data-builder-selection-panel]');
      this.stepper = root.querySelector('[data-builder-stepper]');
      this.title = root.querySelector('[data-builder-title]');
      this.eyebrow = root.querySelector('[data-builder-step-eyebrow]');
      this.counter = root.querySelector('[data-builder-counter]');
      this.mobileNav = root.querySelector('[data-builder-mobile-nav]');
      this.videoTemplate = root.querySelector('[data-builder-popup-video-template]');
      this.addingOverlay = this.createAddingOverlay();
      this.isOpen = false;
      this.isAdding = false;
      this.currentDrag = null;
      this.noticeTimeout = null;
      this.imageControlsTimer = null;
      this.imageControlsOpen = false;
      this.dragHintTimeout = null;
      this.dragHintFadeTimeout = null;
      this.dragHintVisible = false;
      this.state = this.restoreState();
      this.portalOverlayElements();

      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);
      this.handleInput = this.handleInput.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.syncMinimizedWithCartDrawer = this.syncMinimizedWithCartDrawer.bind(this);
      this.handleCartDrawerClose = this.handleCartDrawerClose.bind(this);

      this.root.addEventListener('click', this.handleClick);
      this.root.addEventListener('change', this.handleChange);
      this.root.addEventListener('pointerdown', this.handlePointerDown);
      this.root.addEventListener('input', this.handleInput);
      this.root.addEventListener('focusin', this.handleFocusIn);

      /* O modal e o botão minimizado ficam no body para escapar das camadas da PDP. */
      this.modal?.addEventListener('click', this.handleClick);
      this.modal?.addEventListener('change', this.handleChange);
      this.modal?.addEventListener('pointerdown', this.handlePointerDown);
      this.modal?.addEventListener('input', this.handleInput);
      this.modal?.addEventListener('focusin', this.handleFocusIn);
      this.minimized?.addEventListener('click', this.handleClick);
      this.backdrop?.addEventListener('click', () => this.requestClose());
      document.addEventListener('keydown', this.handleKeyDown);
      document.addEventListener('click', this.handleCartDrawerClose, true);
      document.addEventListener('eurus:cart:items-changed', () => {
        window.setTimeout(() => this.syncMinimizedWithCartDrawer(), 0);
      });

      /* Remove somente marcas deixadas por versões antigas deste configurador.
         A abertura e o fechamento do cart drawer passam a ficar 100% sob
         responsabilidade do Alpine/nativo do tema. */
      this.clearLegacyCartDrawerOverlayState();
      this.render();
    }

    handleCartDrawerClose(event) {
      const closeTrigger = event.target.closest?.('#CloseCart, #CartDrawer-Overlay, [data-cart-drawer-close]');
      if (!closeTrigger) return;

      /* Aguarda a transição nativa do tema e sincroniza somente o botão minimizado.
         Nenhuma camada do drawer é alterada aqui: isso evita criar um bloqueio invisível. */
      window.setTimeout(() => this.syncMinimizedWithCartDrawer(), 620);
    }

    clearLegacyCartDrawerOverlayState() {
      /*
        Versões anteriores podiam gravar `display: none !important` e a classe
        `eh-cart-drawer-overlay-hidden` diretamente na overlay do tema. Esses
        resíduos não são mais usados. Limpamos apenas essas marcas conhecidas,
        sem alterar #CartDrawer, o elemento fixo do drawer ou o estado Alpine.
      */
      document.querySelectorAll('#CartDrawer-Overlay').forEach((overlay) => {
        overlay.classList.remove('eh-cart-drawer-overlay-hidden');
        ['display', 'visibility', 'opacity', 'pointer-events'].forEach((property) => {
          overlay.style.removeProperty(property);
        });
        if (overlay.getAttribute('aria-hidden') === 'true') overlay.removeAttribute('aria-hidden');
      });
    }

    syncMinimizedWithCartDrawer() {
      if (!this.minimized) return;

      let cartOpen = false;
      let hasMiniCartStore = false;
      try {
        hasMiniCartStore = Boolean(
          window.Alpine
          && typeof window.Alpine.store === 'function'
          && window.Alpine.store('xMiniCart')
        );
        cartOpen = hasMiniCartStore && Boolean(window.Alpine.store('xMiniCart').open);
      } catch (error) {
        hasMiniCartStore = false;
        cartOpen = false;
      }

      /* Em carregamentos sem Alpine pronto, usa somente o drawer como fallback. */
      if (!hasMiniCartStore) {
        const drawer = document.getElementById('CartDrawer');
        if (drawer) {
          const style = window.getComputedStyle(drawer);
          cartOpen = style.display !== 'none' && style.visibility !== 'hidden';
        }
      }

      this.minimized.classList.toggle('is-cart-drawer-open', cartOpen);
      this.minimized.setAttribute('aria-hidden', cartOpen ? 'true' : 'false');
    }

    createAddingOverlay() {
      let overlay = this.root.querySelector('[data-builder-adding-overlay]');
      if (overlay) return overlay;

      overlay = document.createElement('div');
      overlay.className = 'eh-necklace-builder__adding-overlay';
      overlay.setAttribute('data-builder-adding-overlay', '');
      overlay.setAttribute('role', 'status');
      overlay.setAttribute('aria-live', 'polite');
      overlay.setAttribute('aria-atomic', 'true');
      overlay.setAttribute('aria-label', 'Adicionando produtos ao carrinho');
      overlay.hidden = true;
      overlay.innerHTML = `
        <div class="eh-necklace-builder__adding-overlay-card">
          <span class="eh-necklace-builder__adding-spinner" aria-hidden="true"></span>
          <span class="eh-necklace-builder__adding-label">Adicionando...</span>
        </div>
      `;
      this.root.appendChild(overlay);
      return overlay;
    }

    setAddingOverlay(show) {
      if (!this.addingOverlay) return;
      this.addingOverlay.hidden = !show;
      this.addingOverlay.setAttribute('aria-hidden', show ? 'false' : 'true');
      document.documentElement.classList.toggle('eh-necklace-builder-adding', Boolean(show));
    }

    portalOverlayElements() {
      if (!document.body) return;

      const cssVariables = [
        '--eh-builder-trigger-bg',
        '--eh-builder-trigger-text',
        '--eh-builder-popup-bg',
        '--eh-builder-popup-text',
        '--eh-builder-accent',
        '--eh-builder-button-bg',
        '--eh-builder-button-text',
        '--eh-builder-button-hover-bg',
        '--eh-builder-button-hover-text'
      ];
      const rootStyles = window.getComputedStyle(this.root);
      const portalNodes = [this.backdrop, this.modal, this.minimized, this.addingOverlay].filter(Boolean);

      portalNodes.forEach((node) => {
        cssVariables.forEach((variable) => {
          const value = rootStyles.getPropertyValue(variable).trim();
          if (value) node.style.setProperty(variable, value);
        });
        node.dataset.ehBuilderPortal = this.config?.builderKey || '';
        if (node.parentElement !== document.body) document.body.appendChild(node);
      });
    }

    ownsNode(node) {
      return Boolean(
        node
        && (
          this.root.contains(node)
          || this.modal?.contains(node)
          || this.minimized?.contains(node)
        )
      );
    }

    readConfig() {
      try {
        const configId = this.root.dataset.builderConfigId;
        const configElement = document.getElementById(configId);
        return configElement ? JSON.parse(configElement.textContent) : null;
      } catch (error) {
        console.error('[ECOMHARD] Não foi possível carregar o configurador de colar.', error);
        return null;
      }
    }

    defaultState() {
      return {
        version: this.config.version,
        step: 1,
        necklace: null,
        charms: [],
        selectedCharmId: null,
        draftVariants: {},
        preview: {
          zoom: 1,
          x: 50,
          y: 50
        },
        minimized: false,
        cartAdded: false,
        notice: ''
      };
    }

    restoreState() {
      const fallback = this.defaultState();
      try {
        const raw = localStorage.getItem(this.config.builderKey);
        if (!raw) return fallback;
        const saved = JSON.parse(raw);
        if (!saved || saved.version !== this.config.version) return fallback;

        const state = {
          ...fallback,
          ...saved,
          draftVariants: saved.draftVariants || {},
          charms: Array.isArray(saved.charms) ? saved.charms : []
        };

        const savedZoom = Number.parseFloat(saved.preview?.zoom);
        const savedX = Number.parseFloat(saved.preview?.x);
        const savedY = Number.parseFloat(saved.preview?.y);
        state.preview = {
          zoom: Number.isFinite(savedZoom) ? clamp(savedZoom, 1, 1.8) : 1,
          x: Number.isFinite(savedX) ? clamp(savedX, 0, 100) : 50,
          y: Number.isFinite(savedY) ? clamp(savedY, 0, 100) : 50
        };

        if (state.necklace) {
          const necklaceProduct = this.getProduct('necklaces', state.necklace.productId);
          const necklaceVariant = necklaceProduct && this.getVariant(necklaceProduct, state.necklace.variantId);
          if (!necklaceProduct || !necklaceVariant) state.necklace = null;
        }

        state.charms = state.charms.filter((instance) => {
          const charmProduct = this.getProduct('charms', instance.productId);
          const charmVariant = charmProduct && this.getVariant(charmProduct, instance.variantId);
          return Boolean(charmProduct && charmVariant);
        });

        if (!state.necklace) {
          state.step = 1;
          state.charms = [];
          state.selectedCharmId = null;
          state.minimized = false;
          state.cartAdded = false;
        }

        if (![1, 2, 3].includes(Number(state.step))) state.step = 1;
        if (!state.charms.some((instance) => instance.id === state.selectedCharmId)) state.selectedCharmId = null;
        state.notice = '';
        return state;
      } catch (error) {
        return fallback;
      }
    }

    saveState() {
      const serializableState = {
        ...this.state,
        notice: ''
      };
      try {
        localStorage.setItem(this.config.builderKey, JSON.stringify(serializableState));
      } catch (error) {
        console.warn('[ECOMHARD] Não foi possível salvar a montagem do colar.', error);
      }
    }

    clearState() {
      try {
        localStorage.removeItem(this.config.builderKey);
      } catch (error) {
        // No action needed when the browser blocks localStorage.
      }
    }

    getProduct(type, productId) {
      const collection = type === 'necklaces' ? this.config.necklaces : this.config.charms;
      return collection.find((product) => sameId(product.id, productId)) || null;
    }

    getVariant(product, variantId) {
      return product?.variants?.find((variant) => sameId(variant.id, variantId)) || null;
    }

    getDefaultVariant(product) {
      return product?.variants?.find((variant) => variant.available) || product?.variants?.[0] || null;
    }

    getDraftVariant(product) {
      const draftId = this.state.draftVariants[String(product.id)];
      const draftVariant = this.getVariant(product, draftId);
      const fallbackVariant = this.getDefaultVariant(product);
      if (!draftVariant && fallbackVariant) {
        this.state.draftVariants[String(product.id)] = fallbackVariant.id;
      }
      return draftVariant || fallbackVariant;
    }

    getSelectedNecklaceProduct() {
      return this.state.necklace ? this.getProduct('necklaces', this.state.necklace.productId) : null;
    }

    getSelectedNecklaceVariant() {
      const product = this.getSelectedNecklaceProduct();
      return product && this.state.necklace ? this.getVariant(product, this.state.necklace.variantId) : null;
    }

    getCharmLimit() {
      const product = this.getSelectedNecklaceProduct();
      const rawLimit = String(product?.limit ?? '').trim();
      const match = rawLimit.match(/\d+/);
      const limit = match ? Number.parseInt(match[0], 10) : 0;
      return Number.isFinite(limit) && limit > 0 ? limit : 0;
    }

    getCharmScale() {
      const product = this.getSelectedNecklaceProduct();
      const rawScale = String(product?.charmScale ?? '').trim().replace(',', '.');
      const match = rawScale.match(/\d+(?:\.\d+)?/);
      const scale = match ? Number.parseFloat(match[0]) : 0;
      return Number.isFinite(scale) && scale > 0 ? clamp(scale, 1, 100) : 8;
    }

    getCharmCount(productId = null) {
      if (!productId) return this.state.charms.length;
      return this.state.charms.filter((instance) => sameId(instance.productId, productId)).length;
    }

    productHasVariantSelectors(product) {
      return product?.variants?.length > 1 && product?.options?.length > 0;
    }

    getVariantImageKey(product, variant) {
      if (!product || !variant) return '';
      const letterIndex = product.options.findIndex((optionName) => normalizeText(optionName) === 'letra');
      if (letterIndex >= 0 && variant.options?.[letterIndex]) return variant.options[letterIndex];
      if (product.options?.length === 1 && variant.options?.[0] && normalizeText(variant.options[0]) !== 'default-title') {
        return variant.options[0];
      }
      return '';
    }

    getMonteImage(product, variant) {
      const images = product?.images || [];
      const variantKey = this.getVariantImageKey(product, variant);
      const exactVariantAlt = variantKey ? normalizeText(`#monte-${variantKey}`) : '';
      const exactBaseAlt = normalizeText('#monte');

      if (exactVariantAlt) {
        const byVariant = images.find((image) => normalizeText(image.alt) === exactVariantAlt);
        if (byVariant) return byVariant;
      }

      const byBase = images.find((image) => normalizeText(image.alt) === exactBaseAlt);
      if (byBase) return byBase;

      return images[0] || null;
    }

    open() {
      this.isOpen = true;
      this.state.minimized = false;
      this.saveState();
      this.setExitConfirmation(false);
      this.modal.hidden = false;
      this.backdrop.hidden = false;
      document.documentElement.classList.add('eh-necklace-builder-open');
      document.body.classList.add('eh-necklace-builder-open');
      this.render();
      window.setTimeout(() => this.modal?.querySelector('[data-builder-action="request-close"]')?.focus(), 20);
    }

    closeModal({ keepAddingOverlay = false } = {}) {
      this.isOpen = false;
      this.setExitConfirmation(false);
      this.modal.hidden = true;
      this.backdrop.hidden = true;
      document.documentElement.classList.remove('eh-necklace-builder-open');
      document.body.classList.remove('eh-necklace-builder-open');
      if (!keepAddingOverlay) this.setAddingOverlay(false);
      this.dragHintVisible = false;
      window.clearTimeout(this.dragHintTimeout);
      window.clearTimeout(this.dragHintFadeTimeout);
      this.renderMinimized();
    }

    requestClose() {
      if (!this.isOpen || this.isAdding || !this.modal || this.modal.hidden) return;
      this.setExitConfirmation(true);
    }

    setExitConfirmation(show) {
      if (!this.exitConfirmation) return;
      this.exitConfirmation.hidden = !show;
      this.modalSurface?.parentElement?.classList.toggle('is-confirming', show);
      if (show) {
        window.setTimeout(() => this.exitConfirmation.querySelector('[data-builder-action="cancel-close"]')?.focus(), 10);
      }
    }

    discardAndClose() {
      this.clearState();
      this.state = this.defaultState();
      this.closeModal();
      this.render();
    }

    showNotice(message) {
      this.state.notice = message || '';
      this.renderPanel();
      window.clearTimeout(this.noticeTimeout);
      if (message) {
        this.noticeTimeout = window.setTimeout(() => {
          this.state.notice = '';
          this.renderPanel();
        }, 3500);
      }
    }

    selectNecklace(productId, variantId) {
      const product = this.getProduct('necklaces', productId);
      const variant = this.getVariant(product, variantId) || this.getDefaultVariant(product);
      if (!product || !variant || !variant.available) {
        this.showNotice(this.config.strings.outOfStock);
        return;
      }

      const previousNecklaceId = this.state.necklace?.productId;
      const previousLimit = this.getCharmLimit();
      this.state.necklace = {
        productId: product.id,
        variantId: variant.id
      };
      this.state.draftVariants[String(product.id)] = variant.id;

      const nextLimit = this.getCharmLimit();
      if (!sameId(previousNecklaceId, product.id) && previousLimit !== nextLimit && this.state.charms.length > nextLimit && nextLimit > 0) {
        this.state.charms = this.state.charms.slice(0, nextLimit);
      }

      this.state.selectedCharmId = this.state.charms.some((item) => item.id === this.state.selectedCharmId)
        ? this.state.selectedCharmId
        : null;
      this.state.notice = '';
      this.saveState();
      this.render();
    }

    setProductOption(productId, optionIndex, optionValue) {
      const type = this.getProduct('necklaces', productId) ? 'necklaces' : 'charms';
      const product = this.getProduct(type, productId);
      if (!product) return;

      const current = this.getDraftVariant(product) || this.getDefaultVariant(product);
      const values = [...(current?.options || [])];
      values[Number(optionIndex)] = optionValue;

      const variant = product.variants.find((item) => item.options.every((value, index) => value === values[index]))
        || product.variants.find((item) => item.options[Number(optionIndex)] === optionValue && item.available)
        || product.variants.find((item) => item.options[Number(optionIndex)] === optionValue);

      if (!variant) return;
      this.state.draftVariants[String(product.id)] = variant.id;

      if (type === 'necklaces' && this.state.necklace && sameId(this.state.necklace.productId, product.id)) {
        this.state.necklace.variantId = variant.id;
      }
      this.saveState();
      this.render();
    }

    addCharm(productId) {
      const product = this.getProduct('charms', productId);
      const variant = this.getDraftVariant(product);
      const limit = this.getCharmLimit();
      if (!this.state.necklace) {
        this.goToStep(1);
        return;
      }
      if (!product || !variant || !variant.available) {
        this.showNotice(this.config.strings.outOfStock);
        return;
      }
      if (!limit) {
        this.showNotice(this.config.strings.limitMissing);
        return;
      }
      if (this.state.charms.length >= limit) {
        this.showNotice(this.config.strings.limitReached);
        return;
      }

      const itemIndex = this.state.charms.length;
      const xOffset = ((itemIndex % 3) - 1) * 10;
      const yOffset = (Math.floor(itemIndex / 3) % 2) * 10;
      const instance = {
        id: makeId(),
        productId: product.id,
        variantId: variant.id,
        x: clamp(50 + xOffset, 10, 90),
        y: clamp(50 + yOffset, 12, 88),
        rotation: 0
      };
      this.state.charms.push(instance);
      this.state.selectedCharmId = instance.id;
      this.state.notice = '';
      this.saveState();
      this.render();
      this.showDragHint();
    }

    selectCharmProduct(productId) {
      const instances = this.state.charms.filter((instance) => sameId(instance.productId, productId));
      if (!instances.length) {
        this.addCharm(productId);
        return;
      }
      this.state.selectedCharmId = instances[instances.length - 1].id;
      this.saveState();
      this.renderVisual();
      this.renderPanel();
    }

    removeLastCharm(productId) {
      const index = [...this.state.charms].map((item) => String(item.productId)).lastIndexOf(String(productId));
      if (index === -1) return;
      const [removed] = this.state.charms.splice(index, 1);
      if (this.state.selectedCharmId === removed.id) this.state.selectedCharmId = null;
      this.state.notice = '';
      this.saveState();
      this.render();
    }

    removeCharm(instanceId) {
      this.state.charms = this.state.charms.filter((item) => item.id !== instanceId);
      if (this.state.selectedCharmId === instanceId) this.state.selectedCharmId = null;
      this.saveState();
      this.render();
    }

    setSelectedCharm(instanceId) {
      this.state.selectedCharmId = instanceId;
      this.saveState();
      this.updateCharmSelection();
      this.renderPanel();
    }

    rotateCharm(instanceId, direction) {
      const item = this.state.charms.find((instance) => instance.id === instanceId);
      if (!item) return;
      item.rotation = Number(item.rotation || 0) + (direction * 15);
      this.saveState();
      this.updateCharmPosition(instanceId);
    }

    setCharmInstanceVariant(instanceId, variantId) {
      const item = this.state.charms.find((instance) => instance.id === instanceId);
      const product = item && this.getProduct('charms', item.productId);
      const variant = product && this.getVariant(product, variantId);
      if (!item || !variant || !variant.available) {
        this.showNotice(this.config.strings.outOfStock);
        return;
      }
      item.variantId = variant.id;
      this.state.draftVariants[String(product.id)] = variant.id;
      this.saveState();
      this.render();
    }

    goToStep(step) {
      const intendedStep = Number(step);
      if (intendedStep === 2 && (!this.state.necklace || !this.getCharmLimit())) {
        this.showNotice(!this.state.necklace ? this.config.strings.chooseNecklace : this.config.strings.limitMissing);
        return;
      }
      if (intendedStep === 3 && !this.state.necklace) {
        this.goToStep(1);
        return;
      }
      this.state.step = clamp(intendedStep, 1, 3);
      this.state.notice = '';
      this.saveState();
      this.render();
    }

    render() {
      if (!this.config) return;
      this.renderHeader();
      this.renderMobileNavigation();
      this.renderVisual();
      this.renderPanel();
      this.renderMinimized();
    }

    renderHeader() {
      const titles = {
        1: this.config.strings.chooseNecklace,
        2: this.config.strings.chooseCharm,
        3: this.config.strings.confirm
      };

      if (this.eyebrow) this.eyebrow.textContent = `Passo ${this.state.step}`;
      if (this.title) this.title.textContent = titles[this.state.step] || this.config.strings.title;

      const shouldShowCounter = this.state.step === 2 && Boolean(this.state.necklace);
      if (this.counter) {
        this.counter.hidden = !shouldShowCounter;
        if (shouldShowCounter) this.counter.textContent = `${this.getCharmCount()} de ${this.getCharmLimit()}`;
      }

      if (this.stepper) this.stepper.innerHTML = '';
    }

    renderNavigationAction(action, label, direction, options = {}) {
      const {
        mobile = false,
        disabled = false,
        extraClass = ''
      } = options;
      const icon = direction === 'previous' ? ICONS.chevronLeft : ICONS.chevronRight;
      const baseClass = mobile ? 'eh-necklace-builder__mobile-nav-link ' : '';
      const directionClass = direction === 'previous'
        ? 'eh-necklace-builder__navigation-action--previous'
        : 'eh-necklace-builder__navigation-action--next';

      return `
        <button
          type="button"
          class="${baseClass}eh-necklace-builder__navigation-action ${directionClass} ${extraClass}" 
          data-builder-action="${escapeHtml(action)}"
          ${disabled ? 'disabled' : ''}
        >
          ${direction === 'previous' ? `<span class="eh-necklace-builder__navigation-icon" aria-hidden="true">${icon}</span>` : ''}
          <span class="eh-necklace-builder__navigation-label">${escapeHtml(label)}</span>
          ${direction === 'next' ? `<span class="eh-necklace-builder__navigation-icon" aria-hidden="true">${icon}</span>` : ''}
        </button>
      `;
    }

    renderMobileNavigation() {
      if (!this.mobileNav) return;

      this.mobileNav.classList.toggle('is-confirmation', this.state.step === 3);

      if (this.state.step === 3) {
        const entries = this.getSummaryEntries();
        const total = entries.reduce((sum, entry) => sum + Number(entry.variant.price || 0), 0);

        this.mobileNav.innerHTML = `
          <div class="eh-necklace-builder__mobile-confirmation-bar">
            <div class="eh-necklace-builder__mobile-confirmation-back">
              ${this.renderNavigationAction('go-step-2', 'Voltar para os Charms', 'previous', { mobile: true, extraClass: 'eh-necklace-builder__mobile-nav-link--back' })}
            </div>
            <div class="eh-necklace-builder__mobile-confirmation-total">
              <div class="eh-necklace-builder__total-line">
                <span>${escapeHtml(this.config.strings.priceLabel)}</span>
                <strong>${this.formatMoney(total)}</strong>
              </div>
              ${this.renderPaymentSummary(total)}
            </div>
            <button type="button" class="eh-necklace-button eh-necklace-button--add eh-necklace-builder__mobile-add-button" data-builder-action="add-to-cart" ${this.isAdding || !this.state.necklace ? 'disabled' : ''}>
              ${this.isAdding ? 'Adicionando...' : escapeHtml(this.config.strings.addToCartButton)}
            </button>
          </div>
        `;
        return;
      }

      const previous = this.state.step === 2
        ? this.renderNavigationAction('go-step-1', 'Voltar para o colar', 'previous', { mobile: true, extraClass: 'eh-necklace-builder__mobile-nav-link--back' })
        : '<span class="eh-necklace-builder__mobile-nav-spacer"></span>';

      const next = this.state.step === 1
        ? this.renderNavigationAction('go-step-2', this.config.strings.chooseCharmsButton, 'next', { mobile: true, disabled: !(this.state.necklace && this.getSelectedNecklaceVariant()), extraClass: 'eh-necklace-builder__mobile-nav-link--next' })
        : this.renderNavigationAction('go-step-3', this.config.strings.confirmButton, 'next', { mobile: true, extraClass: 'eh-necklace-builder__mobile-nav-link--next' });

      this.mobileNav.innerHTML = `
        ${previous}
        <span class="eh-necklace-builder__mobile-brand">${escapeHtml(this.root.querySelector('.eh-necklace-builder__brand')?.textContent?.trim() || '')}</span>
        ${next}
      `;
    }

    isMobileViewport() {
      return typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 749px)').matches;
    }

    getPreviewSettings() {
      const zoom = Number.parseFloat(this.state.preview?.zoom);
      const x = Number.parseFloat(this.state.preview?.x);
      const y = Number.parseFloat(this.state.preview?.y);
      const forceMaxY = this.isMobileViewport();
      return {
        zoom: Number.isFinite(zoom) ? clamp(zoom, 1, 1.8) : 1,
        x: Number.isFinite(x) ? clamp(x, 0, 100) : 50,
        y: forceMaxY ? 100 : (Number.isFinite(y) ? clamp(y, 0, 100) : 50)
      };
    }

    renderDragHint() {
      return `
        <div class="eh-necklace-builder__drag-hint" data-builder-drag-hint role="status" aria-live="polite">
          Segure e arraste o Charm
        </div>
      `;
    }

    getDragHintElement() {
      return this.visualPanel?.querySelector('[data-builder-drag-hint]') || null;
    }

    showDragHint() {
      window.clearTimeout(this.dragHintTimeout);
      window.clearTimeout(this.dragHintFadeTimeout);

      this.dragHintVisible = true;
      this.renderVisual();

      window.requestAnimationFrame(() => {
        this.getDragHintElement()?.classList.add('is-visible');
      });

      this.dragHintTimeout = window.setTimeout(() => {
        this.hideDragHint();
      }, 5000);
    }

    hideDragHint() {
      window.clearTimeout(this.dragHintTimeout);
      window.clearTimeout(this.dragHintFadeTimeout);

      const hint = this.getDragHintElement();
      if (hint) {
        hint.classList.remove('is-visible');
        hint.classList.add('is-leaving');
      }

      this.dragHintFadeTimeout = window.setTimeout(() => {
        this.dragHintVisible = false;
        this.renderVisual();
      }, 360);
    }

    renderImageControls() {
      const preview = this.getPreviewSettings();
      const openClass = this.imageControlsOpen ? ' is-open is-active' : '';
      return `
        <div class="eh-necklace-builder__image-controls${openClass}" data-builder-image-controls>
          <div class="eh-necklace-builder__image-controls-panel" data-builder-image-controls-panel>
            <label class="eh-necklace-builder__image-control">
              <span>Zoom</span>
              <input
                type="range"
                min="1"
                max="1.8"
                step="0.01"
                value="${preview.zoom}"
                data-builder-image-control="zoom"
                aria-label="Zoom da imagem do colar"
              >
            </label>
            <label class="eh-necklace-builder__image-control">
              <span>Posição vertical</span>
              <input
                type="range"
                min="0"
                max="100"
                step="1"
                value="${preview.y}"
                data-builder-image-control="y"
                aria-label="Posição vertical da imagem do colar"
              >
            </label>
          </div>
          <button
            type="button"
            class="eh-necklace-builder__image-controls-toggle"
            data-builder-action="toggle-image-controls"
            aria-label="Abrir controles da imagem"
            aria-expanded="${this.imageControlsOpen ? 'true' : 'false'}"
          >
            <img src="https://cdn.shopify.com/s/files/1/0721/1991/0714/files/arrows.png?v=1783968778" alt="" loading="lazy">
          </button>
        </div>
      `;
    }

    renderVisual() {
      if (!this.visualPanel) return;
      const necklace = this.getSelectedNecklaceProduct();
      const necklaceVariant = this.getSelectedNecklaceVariant();
      let content = '';

      if (necklace && necklaceVariant) {
        const image = this.getMonteImage(necklace, necklaceVariant);
        const preview = this.getPreviewSettings();
        const imageHtml = image
          ? `<img class="eh-necklace-builder__necklace-preview-image" data-builder-necklace-image src="${escapeHtml(image.src)}" alt="${escapeHtml(necklace.title)}" loading="eager" style="--eh-necklace-zoom:${preview.zoom};--eh-necklace-x:${preview.x}%;--eh-necklace-y:${preview.y}%;">`
          : this.videoTemplate?.innerHTML || '';
        const showCharms = this.state.step >= 2;
        const charmInstances = showCharms ? this.state.charms.map((item) => this.renderCharmInstance(item)).join('') : '';
        content = `
          <div class="eh-necklace-builder__visual ${preview.zoom > 1.01 ? 'is-pannable' : ''}" data-builder-visual>
            ${imageHtml}
            ${this.dragHintVisible ? this.renderDragHint() : ''}
            ${showCharms ? `<div class="eh-necklace-builder__canvas" data-builder-canvas>${charmInstances}</div>` : ''}
            ${this.renderImageControls()}
          </div>
        `;
      } else {
        content = `
          <div class="eh-necklace-builder__visual" data-builder-visual>
            ${this.videoTemplate?.innerHTML || ''}
          </div>
        `;
      }

      this.visualPanel.innerHTML = content;
    }

    renderCharmInstance(item) {
      const product = this.getProduct('charms', item.productId);
      const variant = product && this.getVariant(product, item.variantId);
      if (!product || !variant) return '';
      const image = this.getMonteImage(product, variant);
      if (!image) return '';
      const isSelected = item.id === this.state.selectedCharmId;
      const hasOptions = this.productHasVariantSelectors(product);
      const charmScale = this.getCharmScale();
      const variantSelect = hasOptions ? `
        <select class="eh-necklace-builder__charm-variant-select" data-builder-instance-variant="${escapeHtml(item.id)}" aria-label="Alterar variante de ${escapeHtml(product.title)}">
          ${product.variants.map((optionVariant) => `
            <option value="${escapeHtml(optionVariant.id)}" ${sameId(optionVariant.id, variant.id) ? 'selected' : ''} ${optionVariant.available ? '' : 'disabled'}>${escapeHtml(optionVariant.title)}</option>
          `).join('')}
        </select>
      ` : '';

      return `
        <div
          class="eh-necklace-builder__charm-instance ${isSelected ? 'is-selected' : ''} ${hasOptions ? 'has-options' : ''}"
          data-builder-charm-instance="${escapeHtml(item.id)}"
          style="--eh-charm-size:${Number(charmScale)}%;left:${Number(item.x)}%;top:${Number(item.y)}%;transform:translate(-50%, -50%);"
          tabindex="0"
          role="button"
          aria-label="Charm ${escapeHtml(product.title)}. Arraste para posicionar."
        >
          <div class="eh-necklace-builder__charm-rotatable" style="transform:rotate(${Number(item.rotation || 0)}deg);">
            <img class="eh-necklace-builder__charm-image" src="${escapeHtml(image.src)}" alt="${escapeHtml(product.title)}">
          </div>
          <div class="eh-necklace-builder__charm-tools" data-builder-stop-drag>
            <button type="button" class="eh-necklace-builder__charm-tool" data-builder-action="rotate-left" data-instance-id="${escapeHtml(item.id)}" aria-label="Girar para a esquerda" title="Girar para a esquerda">${ICONS.rotateLeft}</button>
            <button type="button" class="eh-necklace-builder__charm-tool" data-builder-action="rotate-right" data-instance-id="${escapeHtml(item.id)}" aria-label="Girar para a direita" title="Girar para a direita">${ICONS.rotateRight}</button>
            ${variantSelect}
            <button type="button" class="eh-necklace-builder__charm-tool" data-builder-action="remove-charm" data-instance-id="${escapeHtml(item.id)}" aria-label="Excluir charm" title="Excluir charm">${ICONS.trash}</button>
          </div>
        </div>
      `;
    }

    renderPanel() {
      if (!this.selectionPanel) return;
      if (this.state.step === 1) {
        this.selectionPanel.innerHTML = this.renderNecklacePanel();
      } else if (this.state.step === 2) {
        this.selectionPanel.innerHTML = this.renderCharmPanel();
      } else {
        this.selectionPanel.innerHTML = this.renderConfirmationPanel();
      }
    }

    renderNecklacePanel() {
      const selected = this.getSelectedNecklaceProduct();
      const selectedVariant = this.getSelectedNecklaceVariant();

      return `
        <div class="eh-necklace-builder__panel-layout">
          <div class="eh-necklace-builder__panel-scroll">
            ${this.renderProductSlider('necklaces')}
            <p class="eh-necklace-builder__notice" data-builder-notice>${escapeHtml(this.state.notice || '')}</p>
          </div>
          <div class="eh-necklace-builder__panel-footer eh-necklace-builder__panel-footer--right">
            ${this.renderNavigationAction('go-step-2', this.config.strings.chooseCharmsButton, 'next', { disabled: !(selected && selectedVariant) })}
          </div>
        </div>
      `;
    }

    renderCharmPanel() {
      return `
        <div class="eh-necklace-builder__panel-layout">
          <div class="eh-necklace-builder__panel-scroll">
            ${this.renderProductSlider('charms')}
            <p class="eh-necklace-builder__notice" data-builder-notice>${escapeHtml(this.state.notice || '')}</p>
          </div>
          <div class="eh-necklace-builder__panel-footer">
            ${this.renderNavigationAction('go-step-1', 'Voltar para o colar', 'previous')}
            ${this.renderNavigationAction('go-step-3', this.config.strings.confirmButton, 'next')}
          </div>
        </div>
      `;
    }

    renderConfirmationPanel() {
      const entries = this.getSummaryEntries();
      const total = entries.reduce((sum, entry) => sum + Number(entry.variant.price || 0), 0);
      const summaryRows = entries.length ? entries.map((entry) => this.renderSummaryRow(entry)).join('') : `
        <p class="eh-necklace-builder__notice">${escapeHtml(this.config.strings.emptyCharms)}</p>
      `;

      return `
        <div class="eh-necklace-builder__panel-layout eh-necklace-builder__panel-layout--confirmation">
          <div class="eh-necklace-builder__panel-scroll eh-necklace-builder__panel-scroll--summary">
            <div class="eh-necklace-builder__summary-head">
              <span></span>
              <span>Nome</span>
              <span>Quantidade</span>
              <span>Preço</span>
            </div>
            <div class="eh-necklace-builder__summary">${summaryRows}</div>
          </div>

          <div class="eh-necklace-builder__confirmation-bottom">
            ${this.renderNavigationAction('go-step-2', 'Voltar para os Charms', 'previous', { extraClass: 'eh-necklace-builder__confirm-back' })}
            <div class="eh-necklace-builder__total">
              <div class="eh-necklace-builder__total-line">
                <span>${escapeHtml(this.config.strings.priceLabel)}</span>
                <strong>${this.formatMoney(total)}</strong>
              </div>
              ${this.renderPaymentSummary(total)}
            </div>
            <button type="button" class="eh-necklace-button eh-necklace-button--add" data-builder-action="add-to-cart" ${this.isAdding || !this.state.necklace ? 'disabled' : ''}>
              ${this.isAdding ? 'Adicionando...' : escapeHtml(this.config.strings.addToCartButton)}
            </button>
          </div>
          <p class="eh-necklace-builder__notice" data-builder-notice>${escapeHtml(this.state.notice || '')}</p>
        </div>
      `;
    }

    renderSummaryRow(entry) {
      const image = this.getMonteImage(entry.product, entry.variant);
      const variantLabel = entry.product.variants.length > 1 ? entry.variant.title : '';
      return `
        <div class="eh-necklace-builder__summary-row">
          <img src="${escapeHtml(image?.src || '')}" alt="${escapeHtml(entry.product.title)}">
          <div class="eh-necklace-builder__summary-info">
            <strong>${escapeHtml(entry.product.title)}</strong>
            ${variantLabel ? `<span>${escapeHtml(variantLabel)}</span>` : ''}
          </div>
          <span class="eh-necklace-builder__summary-quantity">1</span>
          <span class="eh-necklace-builder__summary-price">${this.formatMoney(entry.variant.price)}</span>
        </div>
      `;
    }

    getSummaryEntries() {
      const entries = [];
      const necklaceProduct = this.getSelectedNecklaceProduct();
      const necklaceVariant = this.getSelectedNecklaceVariant();
      if (necklaceProduct && necklaceVariant) {
        entries.push({ product: necklaceProduct, variant: necklaceVariant, type: 'necklace', instanceId: 'necklace' });
      }
      this.state.charms.forEach((instance) => {
        const product = this.getProduct('charms', instance.productId);
        const variant = product && this.getVariant(product, instance.variantId);
        if (product && variant) entries.push({ product, variant, type: 'charm', instanceId: instance.id });
      });
      return entries;
    }

    renderPaymentSummary(total) {
      const payment = this.config.payments || {};
      if (!payment.enabled || !total) return '';
      const lines = [];
      if (payment.pixEnabled && Number(payment.pixDiscount) > 0) {
        const pixTotal = Math.floor((Number(total) * (100 - Number(payment.pixDiscount))) / 100);
        lines.push(`<p>ou <strong>${this.formatMoney(pixTotal)} ${escapeHtml(this.config.strings.pixLabel)}</strong></p>`);
      }

      const maxInstallments = Math.max(2, Number(payment.maxInstallments) || 2);
      const minimumInstallmentCents = Math.max(0, Number(payment.minimumInstallmentCents) || 0);
      let chosenInstallments = 0;
      for (let installments = 2; installments <= 12; installments += 1) {
        if (installments <= maxInstallments) {
          const installmentValue = Math.floor(Number(total) / installments);
          if (installmentValue >= minimumInstallmentCents) chosenInstallments = installments;
        }
      }
      if (chosenInstallments > 0) {
        lines.push(`<p><strong>${chosenInstallments}x de ${this.formatMoney(Math.floor(Number(total) / chosenInstallments))}</strong> ${escapeHtml(this.config.strings.installmentsSuffix)}</p>`);
      }
      return lines.length ? `<div class="eh-necklace-builder__payment-summary">${lines.join('')}</div>` : '';
    }

    formatMoney(cents) {
      const payment = this.config.payments || {};
      const format = payment.showCurrencyCode ? payment.moneyFormatWithCurrency : payment.moneyFormat;
      const amount = Number(cents || 0);
      const placeholderMatch = String(format || '').match(/\{\{\s*(\w+)\s*\}\}/);
      if (!placeholderMatch) return (amount / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

      const token = placeholderMatch[1];
      let precision = 2;
      let thousands = '.';
      let decimal = ',';
      if (token === 'amount_no_decimals' || token === 'amount_no_decimals_with_comma_separator') precision = 0;
      if (token === 'amount_with_comma_separator' || token === 'amount_no_decimals_with_comma_separator') {
        thousands = '.';
        decimal = ',';
      }

      const fixed = (amount / 100).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const formattedWhole = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      const formattedNumber = fraction ? `${formattedWhole}${decimal}${fraction}` : formattedWhole;
      return String(format).replace(placeholderMatch[0], formattedNumber);
    }

    renderProductSlider(type) {
      const products = type === 'necklaces' ? this.config.necklaces : this.config.charms;
      const modifier = type === 'charms' ? 'eh-necklace-builder__product-card--charm' : '';
      return `
        <div class="eh-necklace-builder__product-slider-wrap">
          <div class="eh-necklace-builder__product-slider eh-necklace-builder__product-slider--${type}" data-builder-slider="${type}">
            ${products.map((product) => this.renderProductCard(product, type, modifier)).join('')}
          </div>
        </div>
      `;
    }

    renderProductCard(product, type, modifier) {
      const variant = this.getDraftVariant(product);
      const image = this.getMonteImage(product, variant);
      const isNecklace = type === 'necklaces';
      const selected = isNecklace
        ? this.state.necklace && sameId(this.state.necklace.productId, product.id)
        : this.getCharmCount(product.id) > 0;
      const unavailable = !variant?.available;
      const charmCount = this.getCharmCount(product.id);
      const limitReached = this.getCharmCount() >= this.getCharmLimit();
      const showVariantSelectors = this.productHasVariantSelectors(product);

      return `
        <article
          class="eh-necklace-builder__product-card ${modifier} ${selected ? 'is-selected' : ''} ${unavailable ? 'is-unavailable' : ''}"
          data-builder-product-card
          data-builder-product-type="${type}"
          data-builder-product-id="${escapeHtml(product.id)}"
          tabindex="0"
          role="button"
          aria-label="${isNecklace ? 'Selecionar' : 'Adicionar'} ${escapeHtml(product.title)}"
        >
          <div class="eh-necklace-builder__product-image">
            ${image ? `<img src="${escapeHtml(image.src)}" alt="${escapeHtml(product.title)}" loading="lazy">` : ''}
          </div>
          <div class="eh-necklace-builder__product-details">
            <h4>${escapeHtml(product.title)}</h4>
            <span class="eh-necklace-builder__product-price">${this.formatMoney(variant?.price || 0)}</span>
            ${showVariantSelectors ? this.renderVariantFields(product, variant) : ''}
            ${!isNecklace ? `
              <div class="eh-necklace-builder__card-quantity" data-builder-stop-card>
                <button type="button" data-builder-action="remove-last-charm" data-product-id="${escapeHtml(product.id)}" aria-label="Remover uma unidade de ${escapeHtml(product.title)}" ${charmCount ? '' : 'disabled'}>−</button>
                <span>${charmCount}</span>
                <button type="button" data-builder-action="add-charm" data-product-id="${escapeHtml(product.id)}" aria-label="Adicionar uma unidade de ${escapeHtml(product.title)}" ${(unavailable || limitReached) ? 'disabled' : ''}>+</button>
              </div>
            ` : ''}
            ${unavailable ? `<span class="eh-necklace-builder__product-price">${escapeHtml(this.config.strings.outOfStock)}</span>` : ''}
          </div>
        </article>
      `;
    }

    renderVariantFields(product, selectedVariant) {
      return `
        <div class="eh-necklace-builder__variant-fields" data-builder-stop-card>
          ${product.options.map((optionName, optionIndex) => {
            const values = [...new Set(product.variants.map((variant) => variant.options?.[optionIndex]).filter(Boolean))];
            return `
              <div class="eh-necklace-builder__variant-field">
                <label>${escapeHtml(optionName)}</label>
                <select data-builder-product-option data-product-id="${escapeHtml(product.id)}" data-option-index="${optionIndex}" aria-label="${escapeHtml(optionName)}">
                  ${values.map((value) => {
                    const matchingVariant = product.variants.find((variant) => variant.options?.[optionIndex] === value && variant.available)
                      || product.variants.find((variant) => variant.options?.[optionIndex] === value);
                    const selected = selectedVariant?.options?.[optionIndex] === value;
                    return `<option value="${escapeHtml(value)}" ${selected ? 'selected' : ''} ${matchingVariant?.available ? '' : 'disabled'}>${escapeHtml(value)}</option>`;
                  }).join('')}
                </select>
              </div>
            `;
          }).join('')}
        </div>
      `;
    }

    renderMinimized() {
      if (!this.minimized) return;
      const show = this.state.minimized && !this.isOpen;
      this.minimized.hidden = !show;
      this.syncMinimizedWithCartDrawer();
    }

    updateCharmSelection() {
      this.visualPanel?.querySelectorAll('[data-builder-charm-instance]').forEach((element) => {
        element.classList.toggle('is-selected', element.dataset.builderCharmInstance === this.state.selectedCharmId);
      });
    }

    updateCharmPosition(instanceId) {
      const item = this.state.charms.find((instance) => instance.id === instanceId);
      const element = [...(this.visualPanel?.querySelectorAll('[data-builder-charm-instance]') || [])]
        .find((node) => node.dataset.builderCharmInstance === instanceId);
      if (!item || !element) return;
      element.style.left = `${Number(item.x)}%`;
      element.style.top = `${Number(item.y)}%`;
      element.style.transform = 'translate(-50%, -50%)';
      const rotatable = element.querySelector('.eh-necklace-builder__charm-rotatable');
      if (rotatable) rotatable.style.transform = `rotate(${Number(item.rotation || 0)}deg)`;
    }

    updateNecklacePreview() {
      const preview = this.getPreviewSettings();
      const image = this.visualPanel?.querySelector('[data-builder-necklace-image]');
      if (image) {
        image.style.setProperty('--eh-necklace-zoom', String(preview.zoom));
        image.style.setProperty('--eh-necklace-x', `${preview.x}%`);
        image.style.setProperty('--eh-necklace-y', `${preview.y}%`);
        image.closest('[data-builder-visual]')?.classList.toggle('is-pannable', preview.zoom > 1.01);
      }

      this.visualPanel?.querySelectorAll('[data-builder-image-control]').forEach((input) => {
        const control = input.dataset.builderImageControl;
        input.value = control === 'zoom' ? String(preview.zoom) : String(preview.y);
      });
    }

    setImageControl(control, value) {
      const numericValue = Number.parseFloat(value);
      if (!Number.isFinite(numericValue)) return;
      if (!this.state.preview) this.state.preview = { zoom: 1, x: 50, y: 50 };
      if (!Number.isFinite(Number.parseFloat(this.state.preview.x))) this.state.preview.x = 50;

      if (control === 'zoom') {
        this.state.preview.zoom = clamp(numericValue, 1, 1.8);
        if (this.state.preview.zoom <= 1.01) this.state.preview.x = 50;
      } else if (control === 'y') {
        this.state.preview.y = this.isMobileViewport() ? 100 : clamp(numericValue, 0, 100);
      } else {
        return;
      }

      this.saveState();
      this.updateNecklacePreview();
    }

    handleClick(event) {
      const actionElement = event.target.closest('[data-builder-action]');
      if (actionElement && this.ownsNode(actionElement)) {
        event.preventDefault();
        event.stopPropagation();
        const action = actionElement.dataset.builderAction;
        const productId = actionElement.dataset.productId;
        const instanceId = actionElement.dataset.instanceId;

        switch (action) {
          case 'open': this.open(); break;
          case 'request-close': this.requestClose(); break;
          case 'cancel-close': this.setExitConfirmation(false); break;
          case 'confirm-close': this.discardAndClose(); break;
          case 'go-step-1': this.goToStep(1); break;
          case 'go-step-2': this.goToStep(2); break;
          case 'go-step-3': this.goToStep(3); break;
          case 'add-charm': this.addCharm(productId); break;
          case 'remove-last-charm': this.removeLastCharm(productId); break;
          case 'remove-charm': this.removeCharm(instanceId); break;
          case 'rotate-left': this.rotateCharm(instanceId, -1); break;
          case 'rotate-right': this.rotateCharm(instanceId, 1); break;
          case 'add-to-cart': this.addAllToCart(); break;
          case 'toggle-image-controls': this.toggleImageControls(); break;
          case 'slider-prev': this.scrollSlider(actionElement.dataset.sliderTarget, -1); break;
          case 'slider-next': this.scrollSlider(actionElement.dataset.sliderTarget, 1); break;
          default: break;
        }
        return;
      }

      const productCard = event.target.closest('[data-builder-product-card]');
      if (!productCard || !this.ownsNode(productCard)) return;
      if (event.target.closest('[data-builder-stop-card], button, select, option, input, label')) return;

      const productType = productCard.dataset.builderProductType;
      const productId = productCard.dataset.builderProductId;
      const product = this.getProduct(productType, productId);
      const variant = this.getDraftVariant(product);
      if (!product || !variant?.available) {
        this.showNotice(this.config.strings.outOfStock);
        return;
      }
      if (productType === 'necklaces') {
        this.selectNecklace(productId, variant.id);
      } else {
        this.selectCharmProduct(productId);
      }
    }

    toggleImageControls() {
      this.imageControlsOpen = !this.imageControlsOpen;
      const controls = this.visualPanel?.querySelector('[data-builder-image-controls]');
      if (!controls) return;

      controls.classList.toggle('is-open', this.imageControlsOpen);
      controls.classList.toggle('is-active', this.imageControlsOpen);
      const toggle = controls.querySelector('.eh-necklace-builder__image-controls-toggle');
      if (toggle) toggle.setAttribute('aria-expanded', this.imageControlsOpen ? 'true' : 'false');
    }

    activateImageControls() {
      const controls = this.visualPanel?.querySelector('[data-builder-image-controls]');
      if (!controls) return;

      this.imageControlsOpen = true;
      window.clearTimeout(this.imageControlsTimer);
      controls.classList.add('is-open', 'is-active');
      const toggle = controls.querySelector('.eh-necklace-builder__image-controls-toggle');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');

      this.imageControlsTimer = window.setTimeout(() => {
        controls.classList.remove('is-active');
      }, 1800);
    }

    handleInput(event) {
      const imageControl = event.target.closest('[data-builder-image-control]');
      if (!imageControl || !this.ownsNode(imageControl)) return;
      this.activateImageControls();
      this.setImageControl(imageControl.dataset.builderImageControl, imageControl.value);
    }

    handleFocusIn(event) {
      const imageControl = event.target.closest('[data-builder-image-control]');
      if (!imageControl || !this.ownsNode(imageControl)) return;
      this.activateImageControls();
    }

    handleChange(event) {
      const productOption = event.target.closest('[data-builder-product-option]');
      if (productOption && this.ownsNode(productOption)) {
        this.setProductOption(productOption.dataset.productId, productOption.dataset.optionIndex, productOption.value);
        return;
      }

      const instanceVariant = event.target.closest('[data-builder-instance-variant]');
      if (instanceVariant && this.ownsNode(instanceVariant)) {
        this.setCharmInstanceVariant(instanceVariant.dataset.builderInstanceVariant, instanceVariant.value);
      }
    }

    handleKeyDown(event) {
      if (event.key === 'Escape' && this.isOpen && !this.exitConfirmation?.hidden) {
        event.preventDefault();
        this.setExitConfirmation(false);
        return;
      }
      if (event.key === 'Escape' && this.isOpen) {
        event.preventDefault();
        this.requestClose();
        return;
      }
      if ((event.key === 'Enter' || event.key === ' ') && event.target.matches?.('[data-builder-product-card]')) {
        event.preventDefault();
        event.target.click();
      }
    }

    handlePointerDown(event) {
      const imageControls = event.target.closest('[data-builder-image-controls]');
      if (imageControls && this.ownsNode(imageControls)) {
        const isImageControlInput = event.target.closest('[data-builder-image-control]');
        if (isImageControlInput) this.activateImageControls();
        return;
      }

      if (event.target.closest('[data-builder-stop-drag], button, input, select, option')) return;

      const visual = event.target.closest('[data-builder-visual]');
      const charmElement = event.target.closest('[data-builder-charm-instance]');

      /* Ao tocar/clicar no fundo do preview, fecha os controles do charm ativo. */
      if (visual && this.ownsNode(visual) && !charmElement) {
        const preview = this.getPreviewSettings();
        const canPanPreview = window.matchMedia('(max-width: 749px)').matches
          && preview.zoom > 1.01
          && Boolean(visual.querySelector('[data-builder-necklace-image]'));

        if (canPanPreview) {
          event.preventDefault();
          if (this.state.selectedCharmId) {
            this.state.selectedCharmId = null;
            this.updateCharmSelection();
          }
          this.currentDrag = {
            type: 'preview',
            visual,
            startClientX: event.clientX,
            startPreviewX: preview.x,
            zoom: preview.zoom
          };
          visual.classList.add('is-panning');
          window.addEventListener('pointermove', this.handlePointerMove, { passive: false });
          window.addEventListener('pointerup', this.handlePointerUp, { once: true });
          return;
        }

        if (this.state.selectedCharmId) {
          this.state.selectedCharmId = null;
          this.saveState();
          this.updateCharmSelection();
        }
        return;
      }

      if (!charmElement || !this.ownsNode(charmElement)) return;
      const instanceId = charmElement.dataset.builderCharmInstance;
      const activeVisual = this.visualPanel?.querySelector('[data-builder-visual]');
      const item = this.state.charms.find((instance) => instance.id === instanceId);
      if (!activeVisual || !item) return;

      event.preventDefault();
      this.state.selectedCharmId = instanceId;
      this.updateCharmSelection();
      this.saveState();
      this.currentDrag = { type: 'charm', instanceId, visual: activeVisual };
      window.addEventListener('pointermove', this.handlePointerMove, { passive: false });
      window.addEventListener('pointerup', this.handlePointerUp, { once: true });
    }

    handlePointerMove(event) {
      if (!this.currentDrag) return;
      const rect = this.currentDrag.visual.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      event.preventDefault();

      if (this.currentDrag.type === 'preview') {
        const zoomFactor = Math.max(Number(this.currentDrag.zoom || 1) - 1, 0.18);
        const deltaPercent = ((event.clientX - this.currentDrag.startClientX) / rect.width) * 100;
        if (!this.state.preview) this.state.preview = { zoom: 1, x: 50, y: 50 };
        this.state.preview.x = clamp(this.currentDrag.startPreviewX - (deltaPercent / zoomFactor), 0, 100);
        this.updateNecklacePreview();
        return;
      }

      const item = this.state.charms.find((instance) => instance.id === this.currentDrag.instanceId);
      if (!item) return;
      item.x = clamp(((event.clientX - rect.left) / rect.width) * 100, 4, 96);
      item.y = clamp(((event.clientY - rect.top) / rect.height) * 100, 4, 96);
      this.updateCharmPosition(item.id);
    }

    handlePointerUp() {
      if (this.currentDrag?.type === 'preview') {
        this.currentDrag.visual?.classList.remove('is-panning');
      }
      if (this.currentDrag) this.saveState();
      this.currentDrag = null;
      window.removeEventListener('pointermove', this.handlePointerMove);
    }

    scrollSlider(type, direction) {
      const slider = this.selectionPanel?.querySelector(`[data-builder-slider="${type}"]`);
      if (!slider) return;
      slider.scrollBy({ left: slider.clientWidth * 0.82 * direction, behavior: 'smooth' });
    }

    getCartRoot() {
      return window.Shopify?.routes?.root || '/';
    }

    async getCart() {
      const response = await fetch(`${this.getCartRoot()}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error('Não foi possível consultar o carrinho.');
      return response.json();
    }

    async getPreviousBuilderLineKeys() {
      const cart = await this.getCart();
      return cart.items
        .filter((item) => String(item.properties?.['_ecomhard_monte_seu_colar'] || '') === String(this.config.builderKey))
        .map((item) => item.key);
    }

    buildCartItems(revision) {
      const items = [];
      const necklaceProduct = this.getSelectedNecklaceProduct();
      const necklaceVariant = this.getSelectedNecklaceVariant();
      if (!necklaceProduct || !necklaceVariant) return items;

      items.push({
        id: necklaceVariant.id,
        quantity: 1,
        properties: {
          '_ecomhard_monte_seu_colar': this.config.builderKey,
          '_ecomhard_montagem_revisao': revision,
          '_ecomhard_montagem_item': 'necklace'
        }
      });

      this.state.charms.forEach((instance) => {
        const product = this.getProduct('charms', instance.productId);
        const variant = product && this.getVariant(product, instance.variantId);
        if (!product || !variant) return;
        items.push({
          id: variant.id,
          quantity: 1,
          properties: {
            '_ecomhard_monte_seu_colar': this.config.builderKey,
            '_ecomhard_montagem_revisao': revision,
            '_ecomhard_montagem_item': instance.id
          }
        });
      });

      return items;
    }

    getCartSectionDefinitions() {
      if (window.Alpine && typeof window.Alpine.store === 'function') {
        const helper = window.Alpine.store('xCartHelper');
        if (helper && typeof helper.getSectionsToRender === 'function') {
          return helper.getSectionsToRender() || [];
        }
      }
      return [];
    }

    getCartSectionIds(sectionDefinitions = []) {
      return [...new Set(sectionDefinitions.map((section) => section?.id).filter(Boolean))];
    }

    async fetchCartSections(sectionIds) {
      const ids = [...new Set((sectionIds || []).filter(Boolean))];
      if (!ids.length) return null;

      const url = new URL(window.location.href);
      url.searchParams.set('sections', ids.join(','));
      /* Evita que o navegador reutilize uma resposta anterior do drawer. */
      url.searchParams.set('_eh_cart_refresh', String(Date.now()));

      const response = await fetch(url.toString(), {
        headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' },
        credentials: 'same-origin',
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error('Não foi possível atualizar o carrinho.');
      }

      return response.json();
    }

    getRenderedSectionInnerHTML(sectionHtml, selector) {
      if (!sectionHtml || !selector) return null;
      const documentFragment = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const source = documentFragment.querySelector(selector);
      return source ? source.innerHTML : null;
    }

    applyCartSections(sections, sectionDefinitions = []) {
      if (!sections || !sectionDefinitions.length) return false;
      let applied = false;

      sectionDefinitions.forEach((section) => {
        const sectionHtml = sections[section.id];
        if (!sectionHtml || !section.selector) return;

        section.selector.split(',').forEach((rawSelector) => {
          const selector = rawSelector.trim();
          const destination = document.querySelector(selector);
          const sourceHtml = this.getRenderedSectionInnerHTML(sectionHtml, selector);

          if (destination && sourceHtml !== null) {
            destination.innerHTML = sourceHtml;
            applied = true;
          }
        });
      });

      return applied;
    }

    async removeLineKeys(lineKeys, sectionIds = []) {
      if (!lineKeys.length) return null;

      const updates = lineKeys.reduce((result, key) => ({ ...result, [key]: 0 }), {});
      const body = {
        updates,
        sections_url: window.location.pathname
      };

      if (sectionIds.length) body.sections = sectionIds;

      const response = await fetch(`${this.getCartRoot()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (!response.ok || data?.status === 422) {
        throw new Error(data?.description || data?.message || 'Não foi possível substituir a montagem anterior.');
      }

      return data;
    }

    async refreshCartSections(cart, providedSections = null, sectionDefinitions = []) {
      const helper = window.Alpine && typeof window.Alpine.store === 'function'
        ? window.Alpine.store('xCartHelper')
        : null;
      const sectionIds = this.getCartSectionIds(sectionDefinitions);
      let sections = providedSections;

      if (!sections || !Object.keys(sections).length) {
        sections = await this.fetchCartSections(sectionIds);
      }

      const rendered = this.applyCartSections(sections, sectionDefinitions);

      if (!rendered && helper && typeof helper.reRenderSections === 'function') {
        await helper.reRenderSections(sections || undefined);
      }

      if (helper) helper.currentItemCount = Number(cart?.item_count || 0);
      document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
      return cart;
    }

    /*
      O drawer é atualizado pelo próprio `xCartHelper.reRenderSections`, o
      mesmo fluxo usado pelo tema ao adicionar um produto comum. Não trocamos
      nem reidratamos manualmente filhos de #CartDrawer: no mobile isso podia
      manter o contêiner fixo do drawer visível depois do fechamento.
    */
    async renderCartWithTheme(finalSections, finalCart) {
      const helper = window.Alpine && typeof window.Alpine.store === 'function'
        ? window.Alpine.store('xCartHelper')
        : null;

      this.clearLegacyCartDrawerOverlayState();

      if (helper && typeof helper.reRenderSections === 'function' && finalSections) {
        await helper.waitForCartUpdate?.();
        helper.reRenderSections(finalSections);
        helper.currentItemCount = Number(finalCart?.item_count || 0);
      } else {
        /* Fallback somente caso o helper do tema ainda não esteja disponível. */
        const sectionDefinitions = this.getCartSectionDefinitions();
        await this.refreshCartSections(finalCart, finalSections, sectionDefinitions);
      }

      document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
      return true;
    }

    async openCartDrawer() {
      if (window.location.pathname === '/cart') return;

      this.clearLegacyCartDrawerOverlayState();

      await new Promise((resolve) => {
        window.requestAnimationFrame(() => {
          window.setTimeout(resolve, 180);
        });
      });

      try {
        if (!window.Alpine || typeof window.Alpine.store !== 'function') return;
        const miniCart = window.Alpine.store('xMiniCart');
        if (!miniCart) return;

        if (typeof miniCart.openCart === 'function') {
          miniCart.openCart();
          return;
        }

        miniCart.open = true;
        const popup = window.Alpine.store('xPopup');
        if (popup) popup.open = true;
      } catch (error) {
        console.warn('[ECOMHARD] Não foi possível abrir o carrinho lateral.', error);
      }
    }

    async addAllToCart() {
      if (this.isAdding || !this.state.necklace) return;
      const items = this.buildCartItems(String(Date.now()));
      if (!items.length) return;

      this.isAdding = true;
      this.state.notice = '';
      this.setAddingOverlay(true);
      this.renderPanel();

      try {
        const previousLineKeys = await this.getPreviousBuilderLineKeys();
        const sectionDefinitions = this.getCartSectionDefinitions();
        const sectionIds = this.getCartSectionIds(sectionDefinitions);
        const addBody = {
          items,
          sections_url: window.location.pathname
        };

        if (sectionIds.length) addBody.sections = sectionIds;

        const response = await fetch(`${this.getCartRoot()}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'same-origin',
          body: JSON.stringify(addBody)
        });

        const addResponseData = await response.json();
        if (!response.ok || addResponseData?.status === 422) {
          throw new Error(addResponseData?.description || addResponseData?.message || 'Não foi possível adicionar os produtos ao carrinho.');
        }

        /* Remove a montagem anterior somente depois que a nova entrou no carrinho. */
        const removeResponseData = await this.removeLineKeys(previousLineKeys, sectionIds);

        /*
          O /cart/update já devolve as sections finais. Usamos essa resposta antes
          de qualquer nova busca para evitar que o Safari abra o drawer com uma
          versão intermediária (itens novos + montagem anterior) ou vazia.
        */
        const finalResponseData = removeResponseData || addResponseData;
        const finalCart = finalResponseData?.items ? finalResponseData : await this.getCart();
        const finalSections = finalResponseData?.sections && Object.keys(finalResponseData.sections).length
          ? finalResponseData.sections
          : await this.fetchCartSections(sectionIds);

        /*
          Atualiza pelo caminho nativo do Eurus. Isso mantém a árvore Alpine do
          drawer intacta e evita tanto o drawer vazio no mobile quanto a camada
          fixa invisível após fechar o carrinho.
        */
        await this.renderCartWithTheme(finalSections, finalCart);

        this.state.cartAdded = true;
        this.state.minimized = true;
        this.state.notice = '';
        this.saveState();

        /* O loader nunca acompanha a abertura do drawer. */
        this.setAddingOverlay(false);
        this.closeModal();
        await this.openCartDrawer();

        window.setTimeout(() => this.syncMinimizedWithCartDrawer(), 80);
        window.setTimeout(() => this.syncMinimizedWithCartDrawer(), 620);
        await new Promise((resolve) => window.setTimeout(resolve, 120));
      } catch (error) {
        this.showNotice(error.message || 'Não foi possível adicionar a montagem ao carrinho.');
      } finally {
        this.isAdding = false;
        this.setAddingOverlay(false);
        if (this.isOpen) this.renderPanel();
      }
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll?.('[data-ecomhard-necklace-builder]').forEach((root) => {
      if (root.dataset.ehBuilderInitialized === 'true') return;
      root.dataset.ehBuilderInitialized = 'true';
      root.ecomhardNecklaceBuilder = new NecklaceBuilder(root);
    });
  };

  const watchForBuilders = () => {
    initialize(document);
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.matches?.('[data-ecomhard-necklace-builder]')) initialize(node.parentElement || document);
          initialize(node);
        });
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchForBuilders, { once: true });
  } else {
    watchForBuilders();
  }

  window.EcomhardNecklaceBuilder = { initialize };
})();
